import type { AlertMessageKey, AlertSeverity } from '../models/alert';

/** Sentence-case display titles for each alert message key. */
export const ALERT_TITLES: Record<AlertMessageKey, string> = {
  ServiceOutage:                 'Service outage',
  ServiceDegradation:            'Service degradation',
  ScheduledMaintenance:          'Scheduled maintenance',
  DataQualityIssue:              'Data quality issue',
  FeatureTemporarilyUnavailable: 'Feature temporarily unavailable',
  DataRefreshDelay:              'Data refresh delay',
  IssueResolved:                 'Issue resolved',
  MaintenanceComplete:           'Maintenance complete',
  NewFeatureAvailable:           'New feature available',
};

// Localized message templates (canonical reference: SRA Alerts documentation).
// {placeholders} are substituted from the alert's Parameters payload.
export const MESSAGE_TEMPLATES: Record<AlertMessageKey, string> = {
  ServiceOutage:
    'Some features of Surface Reseller Alliance are currently unavailable. Our team is actively investigating and working to restore service as quickly as possible.',
  ServiceDegradation:
    'Some Surface Reseller Alliance features may be slower than expected or may not display the most up-to-date information while we investigate an ongoing issue.',
  ScheduledMaintenance:
    'Surface Reseller Alliance will undergo planned maintenance during a scheduled maintenance window from {maintenanceStart} to {maintenanceEnd}. Some features may be temporarily unavailable during this period.',
  DataQualityIssue:
    'Some data displayed in Surface Reseller Alliance may be incomplete or inaccurate while an issue is being investigated.',
  FeatureTemporarilyUnavailable:
    '{featureDisplayName} is temporarily unavailable while maintenance or corrective actions are being performed.',
  DataRefreshDelay:
    'Certain reports and metrics may not reflect the most recent data due to a delayed refresh. Information will be updated once processing is complete.',
  IssueResolved:
    'An earlier service disruption reported to us has been resolved. All services are now functioning as expected.',
  MaintenanceComplete:
    'Planned maintenance has been successfully completed and all services are available again.',
  NewFeatureAvailable:
    'Introducing {featureDisplayName}, a new capability on the Surface Reseller Alliance to help you {featureDescription}.',
};

/** Display names for feature keys referenced by alert templates. */
export const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  summary: 'Summary',
  'membership-status': 'Your Membership Status',
  revenue: 'Revenue',
  'membership-tiers': 'Membership Tiers',
  'agi-tracking': 'AGI Tracking',
  notifications: 'Notifications',
  'page-customization': 'Page Customization',
  'sales-insights-per-location': 'Sales Insights per Location',
};

/**
 * Sentence fragments used to fill `{featureDescription}` in the
 * `NewFeatureAvailable` template. Only feature keys that have an established
 * description are eligible for that template (per SRA `Alerts.md`).
 */
export const FEATURE_DESCRIPTIONS: Record<string, string> = {
  'agi-tracking': 'track progress toward your AGI goals and incentive milestones',
  notifications: 'stay up to date with important progress and program updates',
  'page-customization': 'tailor your dashboard experience to your needs',
  'sales-insights-per-location': 'view and understand sales performance at the location level',
};

/** Every feature key surfaced by SRA. Suitable for FeatureTemporarilyUnavailable. */
export const ALL_FEATURE_KEYS: readonly string[] = Object.keys(FEATURE_DISPLAY_NAMES);

/** Feature keys eligible for NewFeatureAvailable — i.e. those with a description. */
export const NEW_FEATURE_KEYS: readonly string[] = Object.keys(FEATURE_DESCRIPTIONS);

export const SEVERITY_OPTIONS: AlertSeverity[] = ['Info', 'Success', 'Warning', 'Critical'];

/**
 * Audience tier options offered when authoring an alert.
 * - `None` targets no tier — the alert is scoped strictly to the supplied
 *   Location IDs. Selecting it clears the other tier checkboxes.
 * - `All` targets every partner (no tier restriction). Selecting it also
 *   clears the other tier checkboxes.
 * - The specific tiers stack with any Location IDs (union).
 *
 * This is alerts-specific — the shared tier constants in `constants/tiers.ts`
 * intentionally omit `Authorized`, `All`, and `None`.
 */
export const AUDIENCE_OPTIONS = [
  'All',
  'Authorized',
  'Silver',
  'Gold',
  'Platinum',
  'None',
] as const;

export type AudienceOption = (typeof AUDIENCE_OPTIONS)[number];

/**
 * Human-readable labels for the tier checkboxes. Values in `AUDIENCE_OPTIONS`
 * are also used as identifiers (Set keys, prefill logic, wire mapping), so we
 * keep the raw values stable and route display through this map.
 */
export const AUDIENCE_OPTION_LABELS: Record<AudienceOption, string> = {
  All: 'All tiers',
  Authorized: 'Authorized',
  Silver: 'Silver',
  Gold: 'Gold',
  Platinum: 'Platinum',
  None: 'None',
};

/**
 * Order in which message keys are shown to authors — most-used first.
 * Kept explicit (rather than sorted by any derived signal) so ordering is
 * a deliberate editorial choice.
 */
export const MESSAGE_KEY_DISPLAY_ORDER: readonly AlertMessageKey[] = [
  'ScheduledMaintenance',
  'MaintenanceComplete',
  'NewFeatureAvailable',
  'DataRefreshDelay',
  'ServiceDegradation',
  'FeatureTemporarilyUnavailable',
  'DataQualityIssue',
  'ServiceOutage',
  'IssueResolved',
];

/**
 * Editorial mapping from message key to the severity we associate with it
 * when offering pre-defined messages in the authoring flyout. Severity remains
 * a free field on the persisted Alert — this map is UI-only.
 */
export const MESSAGE_KEY_SEVERITIES: Record<AlertMessageKey, AlertSeverity> = {
  ServiceOutage:                 'Critical',
  ServiceDegradation:            'Critical',
  ScheduledMaintenance:          'Warning',
  DataQualityIssue:              'Warning',
  FeatureTemporarilyUnavailable: 'Warning',
  DataRefreshDelay:              'Info',
  IssueResolved:                 'Success',
  MaintenanceComplete:           'Success',
  NewFeatureAvailable:           'Info',
};

/**
 * Representative parameter values used to render a filled preview of each
 * parameterized message template in the authoring UI. Message keys without
 * parameters are omitted. Values are illustrative only — actual parameters
 * are collected from the author at create time.
 */
export const SAMPLE_PARAMETERS: Partial<Record<AlertMessageKey, Record<string, string>>> = {
  ScheduledMaintenance:          { maintenanceStart: '{start}', maintenanceEnd: '{end}' },
  FeatureTemporarilyUnavailable: { featureDisplayName: '{feature}' },
  NewFeatureAvailable:           { featureDisplayName: '{capability / new feature}' },
};
